import * as THREE from "three";
import { BufferGeometry, Line, Line3, MathUtils, Ray, Vector3 } from "three";
import { generateUUID } from "three/src/math/MathUtils";
import { ArcDimension } from "@/Dimensions/ArcDimension";
import { LineDimension } from "@/Dimensions/LineDimension";
import { IndserObject, IndsertPosition } from "@/core/IndserObject";
import { MLayer } from "@/core/MLayer";
import {
  DBEntity,
  DbLayer,
  DbLine,
  FMathUtils,
  ModelManager,
  ViewManager,
} from "@/CadEngine";
import { BPMathUtils } from "@/utils/BPMathUtils";
import {
  DrawOperateState,
  IDrawOperation,
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
} from "../IDrawOperation";
import { AnchorPointAux } from "@/Dimensions/AnchorPointAuxs";
import { BPLinear } from "@/core/BPLinear";
import { OrthogonalAux } from "@/Dimensions/OrthogonalAux";
import { Context } from "@/core/context";
import {
  DrawOperateLineParams,
  ArcLayoutType,
  CommonColor,
  ActiveInputType,
  CircleArcInfo,
} from "@/core/Parameters";
import { DimensionGroup } from "@/core/DimensionGroup";
import { ProtectRadius } from "./ProtectRadius";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils";
import { TextModel } from "@/core/TextModel";
import { ULabel } from "@/ui/ULabel";

class DrawFanShapedLayout {
  private m_model: ModelManager;
  private m_layer: DbLayer;
  private m_auxLayer: MLayer;
  private m_auxLineModel: THREE.Line | undefined;
  private m_points: THREE.Vector3[] = [];
  private m_locationObject: THREE.Line;
  private m_size = { x: 1, y: 1, z: 1 };
  private view: ViewManager;
  private m_dimension: DimensionGroup;
  private inputValue: string = "";
  private m_specialParams?: any;
  private m_activeInputType: ActiveInputType = ActiveInputType.None;
  private m_inputValue: string = "";
  private m_distanceInputValue: string = "";
  private m_angleInputValue: string = "";
  private m_quadraturePoint: THREE.Vector3 | undefined = undefined;
  private m_isEnter: boolean = false;
  private m_baseCircleArcOptions: CircleArcInfo | undefined;
  private m_color: number;
  private m_arcLineModelsGroup: THREE.Group = new THREE.Group();
  private m_protectRadiusGroup: THREE.Group = new THREE.Group();
  private m_signModelsGroup: THREE.Group = new THREE.Group();
  private m_textModelsGroup: THREE.Group = new THREE.Group();
  private m_context: Context;
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_arcPointsNum: number = 49;
  private get curDir() {
    return this.m_curPoint.clone().sub(this.m_points[0]).normalize();
  }
  private m_baseCircleArcLineModel!: THREE.Line;
  private m_attachedCircleArcLineModel!: THREE.Line;
  private m_baseSignPoints: THREE.Vector3[] = [];
  private m_signPoints: THREE.Vector3[][] = [];
  private m_signMatrixS: THREE.Matrix4[] = [];
  private m_textModel: TextModel;
  private m_TextLabel: ULabel;
  private m_isShift: Boolean = false;

  constructor(
    context: Context,
    layer: DbLayer,
    color: number,
    specialParams?: any
  ) {
    this.m_model = context.ModelManager;
    this.m_layer = layer;
    this.m_color = color;
    this.m_auxLayer = context.SceneManager.AuxModel;
    this.m_locationObject = context.ToolsManager.CreatedLocationLine();
    this.view = context.ViewManager;
    this.m_dimension = new DimensionGroup(this.view, this.m_auxLayer);
    this.m_dimension.Line.SetVaule("");
    this.m_dimension.Arc.SetVaule("");
    this.m_auxLayer.add(this.m_signModelsGroup);
    this.m_auxLayer.add(this.m_protectRadiusGroup);
    this.m_auxLayer.add(this.m_arcLineModelsGroup);
    this.m_auxLayer.add(this.m_textModelsGroup);
    this.m_specialParams = specialParams;
    this.m_context = context;
    this.m_baseCircleArcLineModel = this.createCircleArcModel([]);
    this.m_attachedCircleArcLineModel = this.createCircleArcModel([]);
    this.m_arcLineModelsGroup.add(
      this.m_baseCircleArcLineModel,
      this.m_attachedCircleArcLineModel
    );
    this.m_textModel = new TextModel(this.m_context);
    this.m_TextLabel = new ULabel(
      this.m_context.ViewManager,
      this.m_context.Dom
    );
    this.m_TextLabel.Visibility = true;
  }

  onMouseMove(e: PointerEvent): void {}
  onKeyDown(e: KeyboardEvent): void {
    e.preventDefault();
    if (e.keyCode == 16) {
      this.m_isShift = true;
    }
    if (!this.m_points.length || this.m_points.length > 2) return;
    if (e.key === "Tab") {
      this.m_inputValue = "";
      if (this.m_activeInputType == ActiveInputType.None) {
        this.m_activeInputType = ActiveInputType.Distance;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      } else if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_activeInputType = ActiveInputType.Angle;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #ffffff";
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_activeInputType = ActiveInputType.Distance;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      }
    }
    if (e.key == "Backspace") {
      if (this.m_inputValue.length > 0) {
        this.m_inputValue = this.m_inputValue.substring(
          0,
          this.m_inputValue.length - 1
        );
      }
      if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_dimension.Line.Input.SetVaule(this.m_inputValue);
        if (this.m_inputValue) {
          this.m_distanceInputValue = this.m_inputValue;
        }
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_dimension.Arc.Input.SetVaule(this.m_inputValue);
        if (this.m_inputValue) {
          this.m_angleInputValue = this.m_inputValue;
        }
      }
    } else if ((e.key >= "0" && e.key <= "9") || e.key == ".") {
      if (this.m_activeInputType == ActiveInputType.None) return;
      if (this.m_inputValue?.includes(".") && e.key == ".") return;
      this.m_inputValue += e.key;
      if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_dimension.Line.Input.SetVaule(this.m_inputValue);
        this.m_distanceInputValue = this.m_inputValue;
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_dimension.Arc.Input.SetVaule(this.m_inputValue);
        this.m_angleInputValue = this.m_inputValue;
      }
    } else if (e.key == "Enter") {
      let length = parseFloat(this.m_distanceInputValue) * 0.001;
      let position = this.m_locationObject.position.clone();
      let curDir = position.clone().sub(this.m_points[0]).normalize();
      let angle = curDir.angleTo(new THREE.Vector3(1, 0, 0));
      if (curDir.clone().cross(new THREE.Vector3(1, 0, 0)).z > 0) {
        angle = -angle;
      }
      curDir.applyAxisAngle(new THREE.Vector3(0, 0, 1), -angle);
      curDir.applyAxisAngle(
        new THREE.Vector3(0, 0, 1),
        MathUtils.degToRad(parseFloat(this.m_angleInputValue))
      );
      position = this.m_points[0].clone().add(curDir.multiplyScalar(length));
      this.m_isEnter = true;
      this.m_quadraturePoint = undefined;
      this.OnMovePoint({
        point: position,
        geo: undefined,
        position: IndsertPosition.None,
      });
      this.m_isEnter = false;
      this.m_points.push(position);
      this.view.Render();
    }
  }
  onKeyUp(e: KeyboardEvent): void {
    if (e.keyCode == 16) {
      this.m_isShift = false;
    }
  }

  OnZoom(camera: THREE.Camera) {
    let scale = this.scale;
    if (this.m_points.length > 0) {
      this.m_dimension.Line.SetDimension(
        this.m_points[0],
        this.m_curPoint,
        scale
      );
      this.m_dimension.Arc.SetDimension(
        this.m_points[0],
        new THREE.Vector3(1, 0, 0),
        this.curDir,
        scale
      );
    }
  }
  OnWheel(e?: WheelEvent): void {
    if (this.m_points.length) {
      this.m_dimension.Line.SetDimension(
        this.m_points[0],
        this.m_curPoint,
        this.scale
      );
      this.m_dimension.Arc.SetDimension(
        this.m_points[0],
        new THREE.Vector3(1, 0, 0),
        this.curDir,
        this.scale
      );
    }
  }
  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {}
  OnMovePoint(params: DrawOperationMovePointParams): void {
    this.view.m_context.Dom.style.cursor = "none";
    this.inputValue = "";
    this.m_locationObject.position.copy(params.point);
    this.m_locationObject.visible = true;
    this.m_curPoint = params.point;
    let TextLabelLocation = new THREE.Vector3();
    TextLabelLocation.x = this.m_curPoint.x + this.scale * 35;
    TextLabelLocation.y = this.m_curPoint.y - this.scale * 10;
    this.m_TextLabel.SetVaule(
      "长按Shift锁定正交",
      TextLabelLocation,
      new THREE.Vector3(1, 0, 0)
    );
    if (
      (this.m_context.DrawManager.isEnableQuadrature || this.m_isShift) &&
      !this.m_isEnter &&
      this.m_points.length
    ) {
      let p = this.m_points[0];
      if (this.m_points.length >= 3) {
        p = this.m_points[2];
      }
      this.m_curPoint = this.m_context.ToolsManager.GetOrthogonalityPoint(
        params.point,
        p
      );
    }
    if (!this.m_points.length) return;
    this.updatedAuxLine();
    this.upDatedCircleArcLineModel();
    this.upDatedDimension();
  }
  OnDownPoint(params: DrawOperationMovePointParams): void {
    let { point } = params;
    if (this.m_points.length < 4) {
      if (
        (this.m_context.DrawManager.isEnableQuadrature || this.m_isShift) &&
        params.position == "none"
      ) {
        point = this.m_curPoint;
      } else {
        this.m_curPoint.copy(point);
      }
      if (this.m_points.length < 2) {
        this.m_points.push(point);
      } else if (this.m_baseCircleArcOptions) {
        this.m_points.push(point);
      }
    }
  }
  GetPoints(): Vector3[] {
    return this.m_points;
  }

  Finish(operate: DrawOperateState) {
    this.clear();
    if (operate == DrawOperateState.complete) {
      let addLines: DbLine[][] = [];
      let returnLine: {
        center: { x: number; y: number; z: number } | undefined;
        matrixs: THREE.Matrix[] | undefined;
        isClockwise: boolean | undefined;
        lines: DbLine[][];
      } = {
        center: undefined,
        matrixs: undefined,
        isClockwise: undefined,
        lines: [],
      };

      if (this.m_baseCircleArcOptions && this.m_points.length == 4) {
        if (this.m_specialParams.isConnectingLine) {
          addLines = this.addLines();
        }
        returnLine = {
          center: this.m_baseCircleArcOptions.center,
          isClockwise: this.m_baseCircleArcOptions.clockwise,
          matrixs: this.m_signMatrixS,
          lines: addLines,
        };
      }
      return returnLine;
    } else if (operate == DrawOperateState.cancel) {
    }
    return [];
  }
  private clear() {
    this.view.m_context.ToolsManager.ObjectsClear([
      this.m_locationObject,
      this.m_auxLineModel!,
    ]);
    this.view.m_context.ToolsManager.GroupClear(this.m_signModelsGroup);
    this.view.m_context.ToolsManager.GroupClear(this.m_protectRadiusGroup);
    this.view.m_context.ToolsManager.GroupClear(this.m_arcLineModelsGroup);
    this.view.m_context.ToolsManager.GroupClear(this.m_textModelsGroup);
    this.m_dimension?.dispose();
    this.m_TextLabel?.dispose();
  }
  private get scale() {
    return this.view.ScreenModelScale;
  }

  private updatedAuxLine() {
    if (this.m_auxLineModel) {
      if (this.m_points.length <= 2) {
        this.m_auxLineModel.geometry.setFromPoints([
          this.m_points[0],
          this.m_curPoint,
        ]);
      } else if (this.m_points.length <= 3) {
        this.m_auxLineModel.geometry.setFromPoints([
          this.m_points[2],
          this.m_curPoint,
        ]);
      }

      this.m_auxLineModel!.computeLineDistances();
    } else {
      this.m_auxLineModel =
        this.m_model.m_context.ToolsManager.CreatedAuxLine();
    }
  }
  private upDatedCircleArcLineModel() {
    if (this.m_points.length === 2) {
      this.m_baseCircleArcOptions = this.computerCircleArc(
        this.m_points[0],
        this.m_points[1],
        this.m_curPoint
      );

      if (this.m_baseCircleArcOptions) {
        let points = this.updateArc(this.m_baseCircleArcOptions);
        this.m_baseCircleArcLineModel.geometry.setFromPoints(points);
      }
    } else if (this.m_points.length == 3) {
      if (this.m_baseCircleArcOptions) {
        let options = {
          ...this.m_baseCircleArcOptions,
        };
        options.radius = this.m_curPoint.distanceTo(options.center);
        let points = this.updateArc(options);
        let baseCircleArcLineModelPoints = Array.from(
          this.m_baseCircleArcLineModel.geometry.attributes.position.array
        );
        let p1Start = baseCircleArcLineModelPoints.slice(0, 3);
        let p2Start = baseCircleArcLineModelPoints.slice(-3);
        points.push(
          new THREE.Vector3(...p1Start),
          points[0],
          new THREE.Vector3(...p2Start),
          points[points.length - 1]
        );
        this.m_attachedCircleArcLineModel.geometry.setFromPoints(points);
        this.updatedSignModel();
      }
    }
  }
  private upDatedDimension() {
    let xAxis = new THREE.Vector3(1, 0, 0);
    if (this.m_points.length < 3) {
      this.m_dimension.Arc.SetDimension(
        this.m_points[0],
        new THREE.Vector3(1, 0, 0),
        this.curDir,
        this.scale
      );

      this.m_dimension.Line.SetDimension(
        this.m_points[0],
        this.m_curPoint,
        this.scale
      );
      let angle = this.curDir.clone().angleTo(xAxis);
      if (this.curDir.clone().cross(xAxis).z > 0) {
        angle = Math.PI * 2 - angle;
      }
      angle = (angle / Math.PI) * 180;
      let distance = (this.m_curPoint.distanceTo(this.m_points[0]) * 1000)
        .toFixed(2)
        .toString();
      this.m_distanceInputValue = distance;
      this.m_angleInputValue = angle.toFixed(0).toString();
      this.m_activeInputType = ActiveInputType.None;
      this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
      this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      this.m_inputValue = "";
    } else {
      this.m_dimension.Line.setVisible(false);
      this.m_dimension.Arc.setVisible(false);
    }
  }
  private computerCircleArc(
    p1: THREE.Vector3,
    p2: THREE.Vector3,
    p3: THREE.Vector3
  ) {
    let xAxis = new THREE.Vector3(1, 0, 0);
    let zAxis = new THREE.Vector3(0, 0, 1);
    let vect1 = p2.clone().sub(p1).normalize();
    let center1StartP = new THREE.Vector3().lerpVectors(p1, p2, 0.5);
    let center1EndP = center1StartP
      .clone()
      .add(vect1.clone().applyAxisAngle(zAxis, Math.PI / 2));
    let line1: any = {
      points: [
        center1StartP.x,
        center1StartP.y,
        center1StartP.z,
        center1EndP.x,
        center1EndP.y,
        center1EndP.z,
      ],
    };

    let vect2 = p3.clone().sub(p2).normalize();

    let center2StartP = new THREE.Vector3().lerpVectors(p2, p3, 0.5);
    let center2EndP = center2StartP
      .clone()
      .add(vect2.clone().applyAxisAngle(zAxis, Math.PI / 2));
    let line2: any = {
      points: [
        center2StartP.x,
        center2StartP.y,
        center2StartP.z,
        center2EndP.x,
        center2EndP.y,
        center2EndP.z,
      ],
    };
    let center = FMathUtils.GetLineIntersection(
      line1 as DbLine,
      line2 as DbLine
    );
    if (!center) return undefined;
    let radius = p1.distanceTo(center);
    let startAngle = this.getAngle(p1, center);
    let endAngle = this.getAngle(p2, center);
    let clockwise = true;
    let crossResult = Math.abs(vect2.clone().cross(vect1).z);
    if (crossResult < 0.001) {
      return undefined;
    } else {
      if (vect2.clone().cross(vect1).z > 0) {
        clockwise = false;
      }
      return {
        center,
        radius,
        startAngle,
        endAngle,
        clockwise: clockwise,
      };
    }
  }
  private getAngle(p1: THREE.Vector3, p2: THREE.Vector3) {
    let xAxis = new THREE.Vector3(1, 0, 0);
    let radiusVector = new THREE.Vector3().subVectors(p1, p2).normalize();
    let angle = radiusVector.clone().angleTo(xAxis);
    if (radiusVector.clone().cross(xAxis).z > 0) {
      angle = Math.PI * 2 - angle;
    }
    return angle;
  }
  private updateArc(params: {
    center: THREE.Vector3;
    radius: number;
    startAngle: number;
    endAngle: number;
    clockwise: boolean;
  }) {
    let Arc = new THREE.ArcCurve(
      params.center.x,
      params.center.y,
      params.radius,
      params.startAngle,
      params.endAngle,
      params.clockwise
    );
    let points: THREE.Vector3[] = [];
    let arcPoints = Arc.getPoints(this.m_arcPointsNum);
    for (let i = 0; i < arcPoints.length - 1; i++) {
      const arcPoint = new Vector3(arcPoints[i].x, arcPoints[i].y, 0);
      const nextArcPoint = new Vector3(
        arcPoints[i + 1].x,
        arcPoints[i + 1].y,
        0
      );
      points.push(arcPoint, nextArcPoint);
    }
    return points;
  }
  private updatedSignModel() {
    let { maxInterval, minInterval } = this.m_specialParams;
    if (this.m_baseCircleArcOptions) {
      this.m_baseSignPoints.length = 0;
      this.m_signPoints.length = 0;
      this.m_signMatrixS.length = 0;
      this.m_signModelsGroup = this.resetGroup(this.m_signModelsGroup);
      this.m_protectRadiusGroup = this.resetGroup(this.m_protectRadiusGroup);
      this.m_textModelsGroup = this.resetGroup(this.m_textModelsGroup);
      let baseRadius = this.m_baseCircleArcOptions.radius * 1000;
      let curDistance =
        this.m_curPoint.distanceTo(this.m_baseCircleArcOptions.center) * 1000;
      let dn = 1;
      let verticalDifferenceSum = curDistance - baseRadius;
      let verticalDifference = verticalDifferenceSum / 2;
      if (Math.abs(verticalDifferenceSum) > minInterval) {
        if (verticalDifferenceSum < 0) {
          dn = Math.abs(Math.floor(verticalDifferenceSum / minInterval));
        } else {
          dn = Math.ceil(verticalDifferenceSum / minInterval);
        }
      }
      if (dn > 1) {
        verticalDifference = verticalDifferenceSum / (dn - 1);
        let baseRadius = curDistance / 1000;
        if (verticalDifferenceSum < 0) {
          baseRadius = this.m_baseCircleArcOptions.radius;
        }
        let arc = this.getSignCircleArc(baseRadius);
        if (arc) {
          this.getRowCircleArcPoints(arc);
          this.passSignPointsCreateSignModel(dn - 1, verticalDifference);
        }
      } else {
        let rowRadius = baseRadius;
        let arc = this.getSignCircleArc(rowRadius / 1000);
        if (arc) {
          this.getRowCircleArcPoints(arc);
        }
      }
      this.addSignMatrixS(this.m_signPoints);
    }
  }
  private getSignCircleArc(radius: number) {
    if (this.m_baseCircleArcOptions) {
      let Arc = new THREE.ArcCurve(
        this.m_baseCircleArcOptions.center.x,
        this.m_baseCircleArcOptions.center.y,
        radius,
        this.m_baseCircleArcOptions.startAngle,
        this.m_baseCircleArcOptions.endAngle,
        this.m_baseCircleArcOptions.clockwise
      );
      return Arc;
    }
  }
  private getRowCircleArcPoints(arc: THREE.ArcCurve) {
    let arcLength = arc.getLength() * 1000;
    let segmentNum = Math.ceil(arcLength / this.m_specialParams.maxInterval);
    this.createdSignModel(arc, segmentNum);
  }
  // 保护半径
  private createdProtectRadius(points: THREE.Vector3[][]) {
    if (this.m_specialParams.protectRadius.isCreate === false) {
      return;
    }
    let material = new THREE.LineBasicMaterial({ color: CommonColor.Red });
    let collectionGeometry = new THREE.BufferGeometry().setFromPoints(
      points.flat()
    );
    let protectRadiusModel = new THREE.LineSegments(
      collectionGeometry,
      material
    );
    protectRadiusModel.frustumCulled = false;
    this.m_protectRadiusGroup.add(protectRadiusModel);
  }

  private createdSignModel(arc: THREE.ArcCurve, segmentNum: number) {
    if (!this.m_baseCircleArcOptions) return;
    let points: THREE.Vector3[][] = [];
    let protectRadiusPoints: THREE.Vector3[][] = [];
    for (let i = 0; i <= segmentNum; i++) {
      let point = arc.getPointAt((1 / segmentNum) * i);
      let vec3 = new THREE.Vector3(point.x, point.y, 0);
      this.m_baseSignPoints.push(vec3);
      points.push(this.getSignPoints(vec3));
      protectRadiusPoints.push(
        this.getSignPoints(vec3, this.m_specialParams.protectRadius.radius)
      );
    }
    this.m_signPoints[0] = [...this.m_baseSignPoints];
    let material = new THREE.LineBasicMaterial({ color: CommonColor.White });
    let collectionGeometry = new THREE.BufferGeometry().setFromPoints(
      points.flat()
    );
    let mergeLine = new THREE.LineSegments(collectionGeometry, material);
    mergeLine.frustumCulled = false;
    this.m_signModelsGroup.add(mergeLine);
    this.createdProtectRadius(protectRadiusPoints);
  }
  private getSignPoints(point: THREE.Vector3, radius: number = 0.2) {
    let points: THREE.Vector3[] = [];
    for (let i = 0; i < 36; i++) {
      let angle1 = (Math.PI * 2 * i) / 36;
      let x1 = Math.cos(angle1) * radius + point.x;
      let y1 = Math.sin(angle1) * radius + point.y;
      points.push(new THREE.Vector3(x1, y1, 0));
      let angle2 = (Math.PI * 2 * (i + 1)) / 36;
      let x2 = Math.cos(angle2) * radius + point.x;
      let y2 = Math.sin(angle2) * radius + point.y;
      points.push(new THREE.Vector3(x2, y2, 0));
    }
    return points;
  }
  private passSignPointsCreateSignModel(segment: number, baseSegment: number) {
    if (!this.m_baseCircleArcOptions) return;
    let points: THREE.Vector3[][] = [];
    let protectRadiusPoints: THREE.Vector3[][] = [];
    for (let i = 1; i <= segment; i++) {
      let distance =
        this.m_baseSignPoints[0].distanceTo(
          this.m_baseCircleArcOptions.center
        ) * 1000;
      let radio = Math.abs((baseSegment * i) / distance);
      let rowSignPoints = [];
      for (let j = 0; j < this.m_baseSignPoints.length; j++) {
        let basePoint = this.m_baseSignPoints[j];
        let point = new THREE.Vector3().lerpVectors(
          basePoint,
          this.m_baseCircleArcOptions.center,
          radio
        );
        rowSignPoints.push(point);
        points.push(this.getSignPoints(point));
        protectRadiusPoints.push(
          this.getSignPoints(point, this.m_specialParams.protectRadius.radius)
        );
      }
      this.m_signPoints[i] = rowSignPoints;
    }
    let material = new THREE.LineBasicMaterial({ color: CommonColor.White });
    let collectionGeometry = new THREE.BufferGeometry().setFromPoints(
      points.flat()
    );
    let mergeLine = new THREE.LineSegments(collectionGeometry, material);
    mergeLine.frustumCulled = false;
    this.m_signModelsGroup.add(mergeLine);
    this.createdProtectRadius(protectRadiusPoints);
  }

  private resetGroup(group: THREE.Group) {
    this.m_context.ToolsManager.GroupClear(group);
    group = new THREE.Group();
    this.m_auxLayer.add(group);
    return group;
  }
  private createCircleArcModel(points: THREE.Vector3[]) {
    let geo = new THREE.BufferGeometry();
    geo.setFromPoints(points);
    let mat = new THREE.LineBasicMaterial({ color: CommonColor.White });
    let line = new THREE.LineSegments(geo, mat);
    line.frustumCulled = false;
    return line;
  }
  private getMatrix(
    p: THREE.Vector3,
    center: THREE.Vector3,
    offset: number = 0
  ) {
    let matrix = new THREE.Matrix4();
    let v1 = new THREE.Vector3().subVectors(center, p);
    let xAxis = new THREE.Vector3(1, 0, 0);
    let zAxis = new THREE.Vector3(0, 0, 1);
    let cross = new THREE.Vector3().crossVectors(v1, xAxis);
    let angle = v1.angleTo(xAxis);
    if (cross.z > 0) {
      angle = Math.PI * 2 - angle;
    }
    angle = angle + Math.PI / 2;
    let pos = p.add(v1.clone().negate().setLength(offset));
    let q = new THREE.Quaternion().setFromAxisAngle(zAxis, angle);
    matrix.compose(pos, q, new THREE.Vector3(1, 1, 1));
    return matrix;
  }
  private addSignMatrixS(signPoints: THREE.Vector3[][]) {
    if (!this.m_baseCircleArcOptions) return;
    let baseRowDatas = signPoints[0];
    let baseColumnDatas = [];
    for (let i = 0; i < signPoints.length; i++) {
      let sPoints = signPoints[i];
      baseColumnDatas.push(sPoints[0]);
      for (let j = 0; j < sPoints.length; j++) {
        const point = sPoints[j];
        let matrix = this.getMatrix(point, this.m_baseCircleArcOptions.center);
        this.m_signMatrixS.push(matrix);
      }
    }
    this.createRowText(baseRowDatas);
    this.createColText(baseColumnDatas);
  }
  private addLines() {
    let addLines: DbLine[][] = [];
    if (!this.m_baseCircleArcOptions) return addLines;
    let key = `${this.m_layer.Id}_${this.m_color}`;
    let lines: DbLine[] = this.m_model.Lines[key]?.lines ?? [];
    let indexs: number[] = [];
    for (let i = 0; i < this.m_arcPointsNum; i++) {
      indexs.push(i);
      if (i != 0 && i != this.m_arcPointsNum - 1) {
        indexs.push(i);
      }
    }

    for (let i = 0; i < this.m_signPoints.length; i++) {
      let sPoints = this.m_signPoints[i];
      let arcPoints: number[] = [];
      addLines[i] = [];
      for (let j = 0; j < sPoints.length - 1; j++) {
        let curPoint = sPoints[j];
        let nextPoint = sPoints[j + 1];
        let arc = this.getArc(curPoint, nextPoint);
        if (arc instanceof THREE.ArcCurve) {
          arcPoints = arc
            .getPoints(this.m_arcPointsNum)
            .flatMap((item) => [item.x, item.y, 0]);
          let line = new DbLine({
            id: generateUUID(),
            layerId: this.m_layer.Id,
            blockId: "",
            points: new Float32Array(arcPoints),
            indexes: new Int32Array(indexs),
            color: this.m_color,
            extend_id: generateUUID(),
          });
          line.getBoundingBox();
          line.getBoundingSphere();
          line.block_path = generateUUID();
          lines.push(line);
          addLines[i].push(line);
        }
      }
    }
    if (this.m_model.Lines[key]) {
      this.m_model.Lines[key].lines = lines;
      this.view.m_context.ToolsManager.LinesToModel(
        lines,
        this.m_model.Lines[key].model
      );
    } else {
      this.m_model.Lines[key] = {
        canEdit: true,
        lines: lines,
        model: this.m_model.addLinesToModel(
          this.m_layer.Id,
          this.m_color,
          lines
        ),
      };
    }
    return addLines;
  }
  private getArc(startP: THREE.Vector3, endP: THREE.Vector3) {
    if (!this.m_baseCircleArcOptions) return [];
    let startAngle = this.getAngle(startP, this.m_baseCircleArcOptions.center);
    let endAngle = this.getAngle(endP, this.m_baseCircleArcOptions.center);
    let radius = startP.distanceTo(this.m_baseCircleArcOptions.center);
    let arc = new THREE.ArcCurve(
      this.m_baseCircleArcOptions.center.x,
      this.m_baseCircleArcOptions.center.y,
      radius,
      startAngle,
      endAngle,
      this.m_baseCircleArcOptions.clockwise
    );
    return arc;
  }

  private createRowText(points: THREE.Vector3[]) {
    let textGeos: THREE.BufferGeometry[] = [];
    let textModel!: THREE.Mesh;
    for (let i = 0; i < points.length - 1; i++) {
      let startP = points[i];
      let endP = points[i + 1];
      let arc = this.getArc(startP, endP);
      if (arc instanceof THREE.ArcCurve) {
        let textP = new THREE.Vector3(
          arc.getPoint(0.5).x,
          arc.getPoint(0.5).y,
          0
        );
        let matrix = this.getMatrix(
          textP,
          this.m_baseCircleArcOptions!.center,
          0.5
        );

        if (i == 0) {
          textModel = this.m_textModel.CreateText({
            text: Number(arc.getLength() * 1000).toFixed(2),
          });
        }
        let geo = textModel.geometry.clone();
        geo.applyMatrix4(matrix);
        textGeos.push(geo);
      }
    }
    if (textGeos.length) {
      let mergeGeo = BufferGeometryUtils.mergeBufferGeometries(textGeos, true);
      let tModel = new THREE.Mesh(mergeGeo, textModel.material);
      this.m_textModelsGroup.add(tModel);
    }
  }
  private createColText(points: THREE.Vector3[]) {
    let textGeos: THREE.BufferGeometry[] = [];
    let textModel!: THREE.Mesh;
    for (let i = 0; i < points.length - 1; i++) {
      let startP = points[i];
      let endP = points[i + 1];
      let centerP = new THREE.Vector3().lerpVectors(startP, endP, 0.5);
      let distance = Number(startP.distanceTo(endP) * 1000).toFixed(2);
      let v1 = new THREE.Vector3().subVectors(startP, endP).setLength(0.5);
      let angle = this.getAngle(startP, endP);
      v1.applyAxisAngle(new THREE.Vector3(0, 0, 1), Math.PI / 2);
      let textP = new THREE.Vector3().addVectors(centerP, v1);
      let q = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(0, 0, 1),
        angle - Math.PI
      );
      let matrix = new THREE.Matrix4();
      matrix.compose(textP, q, new THREE.Vector3(1, 1, 1));
      if (i == 0) {
        textModel = this.m_textModel.CreateText({
          text: distance,
        });
      }
      let geo = textModel.geometry.clone();
      geo.applyMatrix4(matrix);
      textGeos.push(geo);
    }
    if (textGeos.length) {
      let mergeGeo = BufferGeometryUtils.mergeBufferGeometries(textGeos, true);
      let tModel = new THREE.Mesh(mergeGeo, textModel.material);
      this.m_textModelsGroup.add(tModel);
    }
  }
}

export { DrawFanShapedLayout, DrawOperateLineParams };
